import { useQuery } from '@tanstack/react-query';
import {
  Building2,
  Users,
  Target,
  MailX,
  DollarSign,
  TrendingUp,
  UserPlus,
  Layers,
  RefreshCw,
} from 'lucide-react';
import { apiClient } from '@/lib/apiClient';
import type { AnalyticsDashboard, ApiResponse } from '@/_types';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

// ── API ────────────────────────────────────────────────────────────────────────

async function fetchAnalyticsDashboard(): Promise<AnalyticsDashboard> {
  const res = await apiClient.get<ApiResponse<AnalyticsDashboard>>('/analytics/dashboard');
  return res.data.data;
}

// ── Formatters ─────────────────────────────────────────────────────────────────

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat('en-US').format(value);
}

function monthLabel(ym: string): string {
  const [year, month] = ym.split('-');
  return new Date(Number(year), Number(month) - 1, 1).toLocaleString('en-US', { month: 'short' });
}

// ── Stat Card ──────────────────────────────────────────────────────────────────

interface StatCardProps {
  label: string;
  value: string;
  icon: React.ElementType;
  colorClass: string;
  subLabel?: string;
}

function StatCard({ label, value, icon: Icon, colorClass, subLabel }: StatCardProps) {
  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-muted-foreground truncate">{label}</p>
            <p className="mt-1.5 text-2xl font-bold tracking-tight">{value}</p>
            {subLabel && (
              <p className="mt-1 text-xs text-muted-foreground">{subLabel}</p>
            )}
          </div>
          <div className={cn('flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg', colorClass)}>
            <Icon className="h-5 w-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function StatCardSkeleton() {
  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 space-y-2">
            <div className="h-4 w-28 animate-pulse rounded bg-muted" />
            <div className="h-7 w-20 animate-pulse rounded bg-muted" />
            <div className="h-3 w-24 animate-pulse rounded bg-muted" />
          </div>
          <div className="h-10 w-10 flex-shrink-0 animate-pulse rounded-lg bg-muted" />
        </div>
      </CardContent>
    </Card>
  );
}

// ── Revenue Bar Chart (pure CSS — no recharts) ─────────────────────────────────

function RevenueBarChart({ data }: { data: { month: string; revenue: number }[] }) {
  const maxRevenue = Math.max(...data.map((d) => d.revenue), 1);

  return (
    <div className="flex items-end gap-3 h-40 pt-6">
      {data.map((d) => {
        const heightPct = d.revenue > 0 ? Math.max((d.revenue / maxRevenue) * 100, 4) : 0;
        return (
          <div key={d.month} className="group flex flex-1 flex-col items-center gap-1.5">
            <div className="relative w-full flex flex-col items-center">
              {/* Hover tooltip */}
              <span className="pointer-events-none absolute -top-7 hidden group-hover:block whitespace-nowrap rounded border bg-card px-2 py-1 text-xs font-medium shadow-md z-10">
                {formatCurrency(d.revenue)}
              </span>
              <div
                className="w-full rounded-t-sm bg-primary/70 hover:bg-primary transition-colors duration-150"
                style={{ height: `${heightPct}%`, minHeight: d.revenue > 0 ? '4px' : '0px' }}
              />
            </div>
            <span className="text-xs text-muted-foreground">{monthLabel(d.month)}</span>
          </div>
        );
      })}
    </div>
  );
}

// ── Page ───────────────────────────────────────────────────────────────────────

export default function AnalyticsPage() {
  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ['admin-analytics-dashboard'],
    queryFn: fetchAnalyticsDashboard,
    staleTime: 5 * 60_000, // 5 min — aggregate queries are expensive
  });

  // ── Loading ──
  if (isLoading) {
    return (
      <div className="space-y-6">
        <PageHeader />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => <StatCardSkeleton key={i} />)}
        </div>
        <div className="grid gap-4 lg:grid-cols-2">
          <Card><CardContent className="p-5 h-56 rounded-xl"><div className="h-full w-full animate-pulse rounded-lg bg-muted/50" /></CardContent></Card>
          <Card><CardContent className="p-5 h-56 rounded-xl"><div className="h-full w-full animate-pulse rounded-lg bg-muted/50" /></CardContent></Card>
        </div>
      </div>
    );
  }

  // ── Error ──
  if (isError || !data) {
    return (
      <div className="space-y-6">
        <PageHeader />
        <div className="flex flex-col items-center justify-center rounded-xl border bg-card p-16 text-center">
          <TrendingUp className="h-10 w-10 text-muted-foreground/30 mb-3" />
          <p className="font-medium text-muted-foreground">Failed to load analytics data.</p>
          <button
            onClick={() => refetch()}
            className="mt-3 flex items-center gap-1.5 text-sm text-primary underline underline-offset-2 hover:no-underline"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            Try again
          </button>
        </div>
      </div>
    );
  }

  const totalPlanOrgs = Object.values(data.organizations_by_plan).reduce((a, b) => a + b, 0);

  const planBadgeClass = (plan: string): string => {
    const map: Record<string, string> = {
      platinum: 'bg-purple-100 text-purple-700 border-purple-200',
      gold:     'bg-amber-100 text-amber-700 border-amber-200',
      silver:   'bg-slate-100 text-slate-600 border-slate-200',
      trial:    'bg-blue-100 text-blue-700 border-blue-200',
    };
    return map[plan] ?? 'bg-muted text-muted-foreground border-border';
  };

  const planBarClass: Record<string, string> = {
    platinum: 'bg-purple-500',
    gold:     'bg-amber-400',
    silver:   'bg-slate-400',
    trial:    'bg-blue-400',
  };

  const statusBadgeClass: Record<string, string> = {
    active:    'bg-green-100 text-green-700 border-green-200',
    trialing:  'bg-blue-100 text-blue-700 border-blue-200',
    past_due:  'bg-orange-100 text-orange-700 border-orange-200',
    cancelled: 'bg-gray-100 text-gray-500 border-gray-200',
    unpaid:    'bg-red-100 text-red-600 border-red-200',
  };

  const allZeroRevenue = data.revenue_last_6_months.every((d) => d.revenue === 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <PageHeader />
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className="flex items-center gap-1.5 rounded-md border bg-card px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors disabled:opacity-50"
        >
          <RefreshCw className={cn('h-3.5 w-3.5', isFetching && 'animate-spin')} />
          Refresh
        </button>
      </div>

      {/* Row 1 — Core platform stats */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Total Organizations"
          value={formatNumber(data.total_organizations)}
          icon={Building2}
          colorClass="bg-blue-50 text-blue-600"
          subLabel={`+${data.new_orgs_this_month} this month`}
        />
        <StatCard
          label="Total Users"
          value={formatNumber(data.total_users)}
          icon={Users}
          colorClass="bg-indigo-50 text-indigo-600"
          subLabel={`+${data.new_users_this_month} this month`}
        />
        <StatCard
          label="Total Leads"
          value={formatNumber(data.total_leads)}
          icon={Target}
          colorClass="bg-sky-50 text-sky-600"
        />
        <StatCard
          label="Unsubscribed Leads"
          value={formatNumber(data.total_unsubscribed)}
          icon={MailX}
          colorClass="bg-red-50 text-red-500"
        />
      </div>

      {/* Row 2 — Revenue & growth */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Revenue This Month"
          value={formatCurrency(data.revenue_this_month)}
          icon={DollarSign}
          colorClass="bg-green-50 text-green-600"
        />
        <StatCard
          label="All-Time Revenue"
          value={formatCurrency(data.revenue_all_time)}
          icon={TrendingUp}
          colorClass="bg-emerald-50 text-emerald-600"
        />
        <StatCard
          label="New Orgs This Month"
          value={formatNumber(data.new_orgs_this_month)}
          icon={Building2}
          colorClass="bg-violet-50 text-violet-600"
        />
        <StatCard
          label="New Users This Month"
          value={formatNumber(data.new_users_this_month)}
          icon={UserPlus}
          colorClass="bg-pink-50 text-pink-600"
        />
      </div>

      {/* Row 3 — Charts */}
      <div className="grid gap-4 lg:grid-cols-2">

        {/* Revenue trend */}
        <Card>
          <CardHeader className="pb-1">
            <CardTitle className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
              Revenue Trend — Last 6 Months
            </CardTitle>
          </CardHeader>
          <CardContent className="px-5 pb-5">
            {allZeroRevenue ? (
              <div className="flex h-40 items-center justify-center text-sm text-muted-foreground">
                No revenue data to display.
              </div>
            ) : (
              <RevenueBarChart data={data.revenue_last_6_months} />
            )}
          </CardContent>
        </Card>

        {/* Organizations by plan */}
        <Card>
          <CardHeader className="pb-1">
            <CardTitle className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
              Organizations by Plan
            </CardTitle>
          </CardHeader>
          <CardContent className="px-5 pb-5 space-y-3.5">
            {Object.entries(data.organizations_by_plan)
              .sort(([, a], [, b]) => b - a)
              .map(([plan, count]) => {
                const pct = totalPlanOrgs > 0 ? Math.round((count / totalPlanOrgs) * 100) : 0;
                return (
                  <div key={plan} className="space-y-1.5">
                    <div className="flex items-center justify-between text-sm">
                      <Badge className={cn('capitalize border font-medium', planBadgeClass(plan))}>
                        {plan}
                      </Badge>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground tabular-nums">{pct}%</span>
                        <span className="font-semibold tabular-nums w-8 text-right">{formatNumber(count)}</span>
                      </div>
                    </div>
                    <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
                      <div
                        className={cn('h-full rounded-full transition-all duration-300', planBarClass[plan] ?? 'bg-primary')}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            {totalPlanOrgs > 0 && (
              <p className="pt-1 text-right text-xs text-muted-foreground tabular-nums">
                Total: {formatNumber(totalPlanOrgs)} organizations
              </p>
            )}
          </CardContent>
        </Card>

      </div>

      {/* Row 4 — Subscription status pills */}
      <Card>
        <CardHeader className="pb-1">
          <div className="flex items-center gap-2">
            <Layers className="h-4 w-4 text-muted-foreground" />
            <CardTitle className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
              Subscription Status Breakdown
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent className="px-5 pb-5">
          <div className="flex flex-wrap gap-3">
            {Object.entries(data.organizations_by_status)
              .filter(([, count]) => count > 0)
              .sort(([, a], [, b]) => b - a)
              .map(([status, count]) => (
                <div
                  key={status}
                  className={cn(
                    'flex items-center gap-2.5 rounded-lg border px-4 py-2.5 text-sm',
                    statusBadgeClass[status] ?? 'bg-muted text-muted-foreground border-border',
                  )}
                >
                  <span className="font-medium capitalize">{status.replace('_', ' ')}</span>
                  <span className="font-bold tabular-nums">{formatNumber(count)}</span>
                </div>
              ))}
            {Object.values(data.organizations_by_status).every((v) => v === 0) && (
              <p className="text-sm text-muted-foreground">No organizations found.</p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ── Shared page header ─────────────────────────────────────────────────────────

function PageHeader() {
  return (
    <div>
      <h1 className="text-2xl font-bold">Analytics</h1>
      <p className="text-sm text-muted-foreground">System-wide platform statistics</p>
    </div>
  );
}
